/** Browser localStorage helpers for saved qimen charts + customer info. */

export type CustomerInfo = {
  name: string;
  phone: string;
  gender: string;
  purpose: string;
  note: string;
};

export type ChartState = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  clockwise: number;
  antiClockwise: number;
};

export type OverlayMap = Record<number, string[]>;

export type SavedRecord = {
  id: string;
  savedAt: string;
  customer: CustomerInfo;
  chart: ChartState;
  overlays: OverlayMap;
  summary: {
    pillars: string;
    dun: string;
    juLabel: string;
    jieqi: string;
  };
};

const STORAGE_KEY = 'qimen-web-app.records.v1';

export function emptyCustomer(): CustomerInfo {
  return { name: '', phone: '', gender: '', purpose: '', note: '' };
}

export function loadRecords(): SavedRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedRecord[];
    if (!Array.isArray(parsed)) return [];
    return parsed.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
  } catch {
    return [];
  }
}

function persist(list: SavedRecord[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function saveRecord(rec: Omit<SavedRecord, 'id' | 'savedAt'> & { id?: string }): SavedRecord {
  const list = loadRecords();
  const now = new Date().toISOString();
  if (rec.id) {
    const idx = list.findIndex((r) => r.id === rec.id);
    const full: SavedRecord = {
      id: rec.id,
      savedAt: now,
      customer: rec.customer,
      chart: rec.chart,
      overlays: rec.overlays,
      summary: rec.summary,
    };
    if (idx >= 0) list[idx] = full;
    else list.unshift(full);
    persist(list);
    return full;
  }
  const full: SavedRecord = {
    id: `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    savedAt: now,
    customer: rec.customer,
    chart: rec.chart,
    overlays: rec.overlays,
    summary: rec.summary,
  };
  list.unshift(full);
  persist(list);
  return full;
}

export function deleteRecord(id: string): void {
  persist(loadRecords().filter((r) => r.id !== id));
}

export function getRecord(id: string): SavedRecord | undefined {
  return loadRecords().find((r) => r.id === id);
}
